import { useQuery } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { gqlClient } from '@/lib';
import { OrganizationRole } from '@/types/gql.generated';
import { createOrganizationMembersQueryKey } from '@/utils/queryKeys';
import type {
  GetOrganizationMembersQuery,
  GetOrganizationMembersQueryVariables,
} from './useOrganizationMembers.generated';

/** A member as returned by the members query (a subset of the full type). */
export type OrgMember =
  GetOrganizationMembersQuery['getOrganizationMembers'][number];

const query = gql`
  query GetOrganizationMembers($input: GetOrganizationMembersInput!) {
    getOrganizationMembers(input: $input) {
      role
      createdAt
      memberName
      phoneNumber
      user {
        id
        name
        email
        avatar
      }
    }
  }
`;

const selector = (data: GetOrganizationMembersQuery) =>
  [...data.getOrganizationMembers].sort((a, b) => {
    const aIsOwner = a.role === OrganizationRole.Owner;
    const bIsOwner = b.role === OrganizationRole.Owner;
    return Number(bIsOwner) - Number(aIsOwner);
  });

export const useOrganizationMembers = (
  organizationId: string,
  enabled = true
) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: createOrganizationMembersQueryKey(organizationId),
    enabled: Boolean(organizationId) && enabled,
    select: selector,
    staleTime: 10_000,
    queryFn: () => {
      return gqlClient.request<
        GetOrganizationMembersQuery,
        GetOrganizationMembersQueryVariables
      >(query, { input: { organizationId } });
    },
  });

  return {
    members: data ?? [],
    isLoading,
    isError,
  };
};
